import React, { useState } from 'react';
import { Box, Button, Grid, MenuItem, Typography } from '@mui/material';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import CustomerCareSelect from '../../../Shared/CustomerCareSelect';
import { ReactComponent as SuccessIcon } from '../../../../images/icons/add_staff_success_icon.svg';
import { ReactComponent as ErrorIcon } from '../../../../images/icons/add_staff_failed_icon.svg';
import { TextInputFormField } from '../../../Shared/TextInputFormField';
import { StatusMessage } from '../../../Hubs/HubDetails/AssignHubController/components/StatusMessage';
import { ReactComponent as EditIcon } from '../../../../images/icons/edit_icon.svg';
import {
  textFieldStyles,
  darkPurple,
  lightBlack,
  lightGrey,
} from '../../../../mainTheme';

const OPTIONS = [
  {
    value: 'facility_admin',
    label: 'Facility admin',
    roleId: 1,
    description:
      'Control roles, changes, and medication schedules for all locations under a facility',
  },
  {
    value: 'location_admin',
    label: 'Location admin',
    roleId: 1,
    description:
      'Control roles, changes, and medication schedules for one location under a facility',
  },
  {
    value: 'caregiver',
    label: 'Caregiver',
    roleId: 3,
    description: "Assist in monitoring the owner's medication intake",
  },
] as const;

const validationSchema = Yup.object({
  first_name: Yup.string()
    .matches(
      /^[A-Za-z ]+$/,
      'First name must contain only alphabetical characters',
    )
    .required('First name is required'),
  last_name: Yup.string()
    .matches(
      /^[A-Za-z ]+$/,
      'Last name must contain only alphabetical characters',
    )
    .required('Last name is required'),
  email: Yup.string().email('Invalid email').required('Email is required'),
  role: Yup.string().required('Role is required'),
  locationId: Yup.number().when(
    ['role', 'currentLocationId'],
    ([role, currentLocationId] = ['', undefined], schema) => {
      if (!currentLocationId && role !== 'facility_admin') {
        return schema.required('Location is required');
      }
      return schema.nullable();
    },
  ),
});

interface StaffModalProps {
  onClose: () => void;
  staff?: any;
  mode: 'edit' | 'invite';
  onSubmit: (values: any) => Promise<void>;
  status: 'idle' | 'success' | 'error';
  locations?: Array<{ id: number; name: string }>;
  currentLocationId?: number;
  isFacilityAdmin?: boolean;
}

export const roleValueMap: Record<string, string> = {
  'Location Administrator': 'location_admin',
  'Facility Administrator': 'facility_admin',
  Caregiver: 'caregiver',
};

export const StaffModal: React.FC<StaffModalProps> = ({
  onClose,
  staff,
  mode,
  onSubmit,
  status,
  locations = [],
  currentLocationId,
  isFacilityAdmin = false,
}) => {
  const [step, setStep] = useState(0); // this will control the step when a user has more than one location to edit
  const [selectedLocationId, setSelectedLocationId] = useState<number | null>(
    null,
  );
  // Filtering OPTIONS based on role
  const filteredOptions = isFacilityAdmin
    ? OPTIONS.filter((opt) => opt.value !== 'facility_admin')
    : OPTIONS;

  const renderMenuItem = (option: (typeof OPTIONS)[number]) => (
    <MenuItem key={option.value} value={option.value}>
      <Box
        sx={{
          maxWidth: '366px',
        }}
      >
        <Typography
          sx={{
            fontWeight: 500,
            fontSize: '14px',
            lineHeight: '20px',
            letterSpacing: '0.2px',
            color: lightBlack,
          }}
        >
          {option.label}
        </Typography>
        <Typography
          sx={{
            fontWeight: 500,
            fontSize: '12px',
            lineHeight: '16px',
            letterSpacing: 0,
            color: lightGrey,
            wordBreak: 'break-word',
            whiteSpace: 'normal',
          }}
        >
          {option.description}
        </Typography>
      </Box>
    </MenuItem>
  );

  const handleStatusClose = () => {
    onClose();
  };
  const getRoleValue = (role: string) => roleValueMap[role] || '';

  // Helper to get location options from staff.location
  const getLocationOptions = () => {
    if (!Array.isArray(staff?.location)) return [];
    return staff.location
      .map((locName: string) => locations.find((l) => l.name === locName))
      .filter(Boolean);
  };

  const getLocationIdFromName = (locationName: string, locations: any[]) => {
    const found = locations.find((loc) => loc.name === locationName);
    return found ? found.id : null;
  };

  // Dynamic values based on mode
  const isEdit = mode === 'edit';
  const title = isEdit ? 'Edit staff' : 'Invite new staff';
  const buttonText = isEdit ? 'Save' : 'Invite';
  const successTitle = isEdit ? 'Staff saved' : 'Invite sent';
  const successMessage = isEdit
    ? 'The staff has been updated.'
    : "Invitations will appear in the recipient's inbox.";
  const errorTitle = isEdit ? "Staff didn't save" : 'Invite didn’t send';

  // If editing and staff has multiple locations, show location picker first
  const shouldShowLocationPicker =
    isEdit &&
    Array.isArray(staff?.location) &&
    staff.location.length > 1 &&
    step === 0;

  const initialValues = isEdit
    ? {
        first_name: staff?.first_name || '',
        last_name: staff?.last_name || '',
        email: staff?.email || '',
        role: getRoleValue(staff?.role),
        locationId:
          staff?.parent_facility_id ?? // This applies for Location Admin role
          selectedLocationId ??
          (Array.isArray(staff?.location) && staff.location.length > 0
            ? getLocationIdFromName(staff.location[0], locations)
            : null),
      }
    : {
        first_name: '',
        last_name: '',
        email: '',
        role: '',
        locationId: currentLocationId || null,
      };

  return (
    <Box>
      {status === 'idle' && (
        <Box display="flex" flexDirection="column" gap={2}>
          <EditIcon />
          {shouldShowLocationPicker ? (
            // Step 1: Location picker
            <Box>
              <Typography
                sx={{
                  fontWeight: 500,
                  fontSize: '22px',
                  lineHeight: '30px',
                  letterSpacing: '0.36px',
                  color: darkPurple,
                  mb: 2,
                }}
              >
                Select a location to edit
              </Typography>
              <CustomerCareSelect
                label="Location"
                value={selectedLocationId || ''}
                onChange={(e) => setSelectedLocationId(Number(e.target.value))}
                fullWidth
              >
                {getLocationOptions().map((location: any) => (
                  <MenuItem key={location.id} value={location.id}>
                    {location.name}
                  </MenuItem>
                ))}
              </CustomerCareSelect>
              <Box display="flex" justifyContent="flex-end" mt={3}>
                <Button
                  variant="contained"
                  color="secondary"
                  disabled={!selectedLocationId}
                  onClick={() => setStep(1)}
                >
                  Next
                </Button>
              </Box>
            </Box>
          ) : (
            // Step 2: Existing Formik form
            <Formik
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={(values) => {
                let payload: any = { ...values };

                // On Edit we need to send the customer ID

                if (isEdit && staff?.customer_id) {
                  payload.customer_id = staff.customer_id;
                }

                onSubmit(payload);
              }}
              validateOnChange={true}
              validateOnBlur={true}
            >
              {({ dirty, values, setFieldValue }) => (
                <Form>
                  <Box display="flex" flexDirection="column" gap={2}>
                    <Typography
                      sx={{
                        fontWeight: 500,

                        fontSize: '22px',

                        lineHeight: '30px',

                        letterSpacing: '0.36px',

                        color: darkPurple,
                      }}
                    >
                      {title}
                    </Typography>

                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Field
                          name="first_name"
                          label="First name"
                          component={TextInputFormField}
                          sx={textFieldStyles}
                        />
                      </Grid>

                      <Grid item xs={6}>
                        <Field
                          name="last_name"
                          label="Last name"
                          component={TextInputFormField}
                          sx={textFieldStyles}
                        />
                      </Grid>
                    </Grid>

                    <Field
                      name="email"
                      label="Email address"
                      component={TextInputFormField}
                      sx={textFieldStyles}
                      InputProps={{
                        readOnly: isEdit,
                      }}
                    />

                    <CustomerCareSelect
                      label="Role"
                      value={values.role}
                      onChange={(e) => {
                        const selectedOption = filteredOptions.find(
                          (opt) => opt.value === e.target.value,
                        );

                        setFieldValue('role', e.target.value);

                        //  store the roleId for API submission since it has same value for multiple options

                        setFieldValue('roleId', selectedOption?.roleId);
                      }}
                      fullWidth
                      renderSelectedValue={(value) => {
                        const option = filteredOptions.find(
                          (opt) => opt.value === value,
                        );

                        return option?.label ?? '';
                      }}
                    >
                      {filteredOptions.map(renderMenuItem)}
                    </CustomerCareSelect>

                    {values.role !== 'facility_admin' &&
                      values.role !== '' &&
                      !isEdit &&
                      !currentLocationId && (
                        <CustomerCareSelect
                          label="Location"
                          value={values.locationId || ''}
                          onChange={(e) =>
                            setFieldValue('locationId', e.target.value)
                          }
                          fullWidth
                          disabled={!!currentLocationId}
                          sx={
                            isEdit
                              ? {
                                  pointerEvents: 'none',

                                  backgroundColor: '#f5f5f5',
                                }
                              : {}
                          }
                        >
                          {locations?.map((location) => (
                            <MenuItem key={location.id} value={location.id}>
                              {location.name}
                            </MenuItem>
                          ))}
                        </CustomerCareSelect>
                      )}

                    <Box display="flex" justifyContent="space-between" mt={3}>
                      <Button
                        variant="text"
                        color="secondary"
                        onClick={onClose}
                      >
                        Cancel
                      </Button>

                      <Button
                        variant="contained"
                        color="secondary"
                        type="submit"
                        disabled={
                          !dirty ||
                          !values.first_name ||
                          !values.last_name ||
                          !values.email ||
                          !values.role ||
                          (values.role !== 'facility_admin' &&
                            !currentLocationId &&
                            !values.locationId)
                        }
                      >
                        {buttonText}
                      </Button>
                    </Box>
                  </Box>
                </Form>
              )}
            </Formik>
          )}
        </Box>
      )}
      {status === 'success' && (
        <StatusMessage
          icon={<SuccessIcon />}
          title={successTitle}
          message={successMessage}
          onClose={handleStatusClose}
        />
      )}
      {status === 'error' && (
        <StatusMessage
          icon={<ErrorIcon />}
          title={errorTitle}
          message="Please try again or come back later."
          onClose={handleStatusClose}
          primaryButtonText="Try again"
        />
      )}
    </Box>
  );
};

export default StaffModal;
